import { z } from "zod";
import { TerminalReachability } from "./terminal.js";

/*
 * The remote desktop (RDP) in the browser (docs/rdp.md): a session answer
 * with a one-use ticket, then a WebSocket on our origin. Text frames carry
 * our JSON messages below; binary frames carry the Guacamole protocol
 * (UTF-8, whole instructions only) between the browser's
 * guacamole-common-js and the gateway (`guacd`), relayed and filtered by
 * the API. The API alone chooses where the gateway connects.
 */

/** The TCP port of the remote desktop on servers (per-server ports come later). */
export const RDP_PORT = 3389;

/** Description of the rule the panel adds to let the remote desktop in. */
export const RDP_RULE_DESCRIPTION = "CloudServer remote desktop";

export const RdpSession = z.object({
  /** Our id of the session, in the audit log. */
  sessionId: z.uuid(),
  /** Path of the WebSocket on our origin, e.g. `/api/v1/rdp/<ticket>`; one use, 30 s. */
  url: z.string().startsWith("/"),
});
export type RdpSession = z.infer<typeof RdpSession>;

/** Port 3389 through the server's firewalls, and whether the gateway is configured at all. */
export const RdpReachability = TerminalReachability.extend({
  /** False when this installation has no RDP gateway (`RDP_GATEWAY`): no remote desktop. */
  gateway: z.boolean(),
});
export type RdpReachability = z.infer<typeof RdpReachability>;

/** Why a remote desktop session ended or could not start (sent before the socket closes). */
export const RdpFailure = z.enum([
  /** The gateway could not reach port 3389 (firewall, RDP off, server still booting). */
  "unreachable",
  "timeout",
  /** Wrong user name or password (Network Level Authentication refused it). */
  "auth_failed",
  /** Signed in, but the account may not use the remote desktop (locked, expired, no rights). */
  "forbidden",
  /** Another connection took over this Windows session. */
  "session_conflict",
  /** The gateway is not running or not configured. */
  "gateway_unavailable",
  /** The gateway failed in another way (see its log). */
  "gateway_error",
  "idle",
  "lifetime",
  "closed",
  "expired",
]);
export type RdpFailure = z.infer<typeof RdpFailure>;

/** A field of the sign-in: no control characters (they mean nothing to Windows). */
const plain = (max: number) =>
  z
    .string()
    .max(max)
    .refine(
      (value) =>
        ![...value].some((c) => {
          const code = c.charCodeAt(0);
          return code < 0x20 || code === 0x7f;
        }),
      "No control characters",
    );

/** Text frames from the browser: one, first. Everything after it is binary Guacamole. */
/**
 * The keyboard layout of the Windows session, as guacd names it
 * (`server-layout`): keys are sent as that layout's scan codes. `failsafe`
 * sends Unicode characters instead, for layouts guacd has no map of
 * (Cyrillic among them).
 */
export const RdpKeyboardLayout = z.enum([
  "en-us-qwerty",
  "en-gb-qwerty",
  "de-de-qwertz",
  "fr-fr-azerty",
  "it-it-qwerty",
  "es-es-qwerty",
  "sv-se-qwerty",
  "ja-jp-qwerty",
  "failsafe",
]);
export type RdpKeyboardLayout = z.infer<typeof RdpKeyboardLayout>;

export const RdpClientMessage = z.object({
  type: z.literal("start"),
  /** The display size in CSS pixels and the screen's DPI; the desktop follows later resizes. */
  width: z.number().int().min(320).max(8192),
  height: z.number().int().min(200).max(8192),
  dpi: z.number().int().min(48).max(480),
  /** Typed for this session only: never stored, never logged. */
  username: plain(256).min(1),
  password: plain(512),
  domain: plain(255).optional(),
  /** IANA zone of the browser, e.g. `Europe/Berlin`, for the Windows session's clock. */
  timezone: z
    .string()
    .regex(/^[A-Za-z0-9_+\-/]{1,64}$/)
    .optional(),
  /** The session's keyboard layout (the person's choice in the session settings). */
  layout: RdpKeyboardLayout.optional(),
});
export type RdpClientMessage = z.infer<typeof RdpClientMessage>;

/** Text frames from the API. */
export const RdpServerMessage = z.discriminatedUnion("type", [
  /** The desktop is drawing: the sign-in worked. */
  z.object({ type: z.literal("ready") }),
  z.object({ type: z.literal("error"), reason: RdpFailure }),
]);
export type RdpServerMessage = z.infer<typeof RdpServerMessage>;

/*
 * The Guacamole protocol's wire format: `LENGTH.VALUE,LENGTH.VALUE;`, the
 * first element the opcode. LENGTH counts Unicode code points, not UTF-16
 * units nor bytes (guacamole-server, `guac_utf8_strlen`).
 */

/** One Guacamole instruction; `raw` is its exact text, to pass it on unchanged. */
export interface GuacInstruction {
  opcode: string;
  args: string[];
  raw: string;
}

export class GuacProtocolError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "GuacProtocolError";
  }
}

const HIGH_SURROGATE = /[\uD800-\uDBFF]/;

/** Code points in a string (a surrogate pair is one). */
export function codePointLength(text: string): number {
  if (!HIGH_SURROGATE.test(text)) return text.length;
  let count = 0;
  for (let i = 0; i < text.length; i++) {
    const unit = text.charCodeAt(i);
    if (unit >= 0xd800 && unit <= 0xdbff && i + 1 < text.length) i++;
    count++;
  }
  return count;
}

/** One instruction as text. */
export function guacEncode(opcode: string, ...args: readonly (string | number)[]): string {
  return `${[opcode, ...args]
    .map((value) => {
      const text = String(value);
      return `${codePointLength(text)}.${text}`;
    })
    .join(",")};`;
}

/** The index after `count` code points from `from`, or null when the text ends first. */
function advance(text: string, from: number, count: number): number | null {
  const optimistic = from + count;
  if (optimistic > text.length) return null;
  if (!HIGH_SURROGATE.test(text.slice(from, optimistic))) return optimistic;
  let index = from;
  for (let n = 0; n < count; n++) {
    if (index >= text.length) return null;
    const unit = text.charCodeAt(index);
    index += unit >= 0xd800 && unit <= 0xdbff ? 2 : 1;
  }
  return index > text.length ? null : index;
}

/** The longest LENGTH prefix we accept (guacd's own limit is far lower). */
const MAX_LENGTH_DIGITS = 9;

/**
 * Splits a stream of Guacamole text into instructions. Text may arrive in
 * any pieces; an incomplete instruction waits for the rest. Throws
 * `GuacProtocolError` on malformed input or when an incomplete instruction
 * grows past `maxPending` UTF-16 units.
 */
export class GuacParser {
  private buffer = "";

  constructor(private readonly maxPending = 1024 * 1024) {}

  /** Text received but not yet a whole instruction. */
  get pending(): number {
    return this.buffer.length;
  }

  receive(text: string): GuacInstruction[] {
    this.buffer += text;
    const instructions: GuacInstruction[] = [];
    let start = 0;
    for (;;) {
      const parsed = this.parseOne(start);
      if (!parsed) break;
      instructions.push(parsed.instruction);
      start = parsed.end;
    }
    this.buffer = this.buffer.slice(start);
    if (this.buffer.length > this.maxPending) {
      throw new GuacProtocolError("Instruction too long");
    }
    return instructions;
  }

  private parseOne(start: number): { instruction: GuacInstruction; end: number } | null {
    const text = this.buffer;
    const elements: string[] = [];
    let index = start;
    for (;;) {
      const dot = text.indexOf(".", index);
      if (dot === -1) {
        if (text.length - index > MAX_LENGTH_DIGITS) {
          throw new GuacProtocolError("Element length too long");
        }
        return null;
      }
      const digits = text.slice(index, dot);
      if (!/^\d+$/.test(digits) || digits.length > MAX_LENGTH_DIGITS) {
        throw new GuacProtocolError("Bad element length");
      }
      const valueStart = dot + 1;
      const valueEnd = advance(text, valueStart, Number(digits));
      if (valueEnd === null || valueEnd >= text.length) return null;
      elements.push(text.slice(valueStart, valueEnd));
      const terminator = text[valueEnd];
      if (terminator === ";") {
        const [opcode = "", ...args] = elements;
        return {
          instruction: { opcode, args, raw: text.slice(start, valueEnd + 1) },
          end: valueEnd + 1,
        };
      }
      if (terminator !== ",") throw new GuacProtocolError("Bad element terminator");
      index = valueEnd + 1;
    }
  }
}
